import { useNavigation } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { PlusIcon } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import {
  type RunsColumnVariant,
  useRunsColumns,
} from "@/pages/tina/runs/hooks/use-runs-columns";
import { buildRunsFilters } from "@/pages/tina/runs/runs-filters";
import type { RunJobKind, RunRead } from "@/types/tina";

const TABS: ReadonlyArray<{
  value: RunsColumnVariant;
  label: string;
  hint: string;
}> = [
  {
    value: "runs",
    label: "Runs",
    hint: "Site scrapes — cron and manual.",
  },
  {
    value: "enrichment",
    label: "Enrichments",
    hint: "External entity-enrichment jobs, one run per intake request or fan-out batch.",
  },
  {
    value: "refresh",
    label: "Refreshes",
    hint: "The nightly sweep re-fetching published events' source pages — one run per batch hour, with daytime duplicate-hook jobs landing in the current hour's run.",
  },
];

/**
 * The runs page, split into scrape runs, enrichment runs and refresh runs.
 *
 * **Scrape is the default tab**, because it is the current behaviour minus the
 * noise: enrichment runs average 1.0 events each against 749.5 for a cron
 * scrape, and on 2026-08-27 they were two thirds of the rows on this page. A
 * refresh run is thousands of jobs in the other direction — the nightly sweep
 * is one run per batch hour, not per event. The tab choice is persisted, so a
 * curator who lives in one of them stays there.
 *
 * There is deliberately **no "All" tab**. `job_kind` absent means every class,
 * so one would be a single extra `TabsTrigger` — but a mixed list cannot be
 * read without a per-row class badge, and mixing three shapes this different
 * (per-site scrapes, per-entity enrichments, per-hour refresh sweeps) is what
 * made the page hard to use in the first place.
 */
export const RunsList = () => {
  const [tab, setTab] = usePersistedState<RunsColumnVariant>(
    "tina-runs:job-kind",
    "runs",
  );
  const columns = useRunsColumns({ variant: tab });
  const { create, show } = useNavigation();

  const table = useTable<RunRead>({
    columns,
    refineCoreProps: {
      resource: "tina-runs",
      pagination: { pageSize: 25 },
      // `job_kind` is always emitted (as `undefined` when it would be cleared),
      // per the convention `buildRunsFilters` documents. Both tabs set it, so
      // nothing is ever left to a stale internal copy.
      filters: { permanent: buildRunsFilters({ jobKind: kindOf(tab) }) },
    },
    meta: {
      onDoubleClick: (_e: React.MouseEvent, row: { original: RunRead }) => {
        show("tina-runs", row.original.id);
      },
    },
  });

  const active = TABS.find((entry) => entry.value === tab) ?? TABS[0];

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Runs</h1>
        <Button onClick={() => create("tina-runs")}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Trigger run
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as RunsColumnVariant)}
        >
          <TabsList>
            {TABS.map((entry) => (
              <TabsTrigger key={entry.value} value={entry.value}>
                {entry.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">{active.hint}</p>
      </div>
      <DataTable table={table} />
    </div>
  );
};

/**
 * Tab → the `job_kind` value to send.
 *
 * A `switch` with an exhaustiveness check, not a ternary chain: a ternary's
 * final `else` defaults every unhandled tab to `"scrape"`, which is exactly
 * the failure `runJobKind` exists to prevent on the read side (Tina
 * re-spelled its scrape arm from `<> 'enrichment'` to `= 'scrape'` in
 * migration `0058` so a third kind could not fall into the scrape tab by
 * default) — a default arm here would silently reintroduce it on the write
 * side for a fourth tab.
 */
const kindOf = (tab: RunsColumnVariant): RunJobKind => {
  switch (tab) {
    case "runs":
      return "scrape";
    case "enrichment":
      return "enrichment";
    case "refresh":
      return "refresh";
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
};
